// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FileTableRow } from '@/components/file-table-row';
import { SupportSnackbar } from '@/components/support-snackbar';
import { UploadFileOutlined } from '@/constants/hardfin-standard-icons';
import { File } from '@/file/api/types';
import { useDisclosure } from '@/hooks/use-disclosure';
import { deleteTicketFile } from '@/ticket/api/delete-ticket-file';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface RowProps {
  ticketKey: string;

  file: File;
}

function TicketFileTableRow(props: RowProps) {
  const dialog = useDisclosure();
  const queryClient = useQueryClient();

  const deleteFileMutation = useMutation({
    mutationFn: () => deleteTicketFile(props.ticketKey, props.file.key),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ticket', props.ticketKey],
      });
      queryClient.invalidateQueries({
        queryKey: ['ticket', props.ticketKey, 'files'],
      });

      dialog.close();
    },
  });

  const handleClickDelete = () => {
    deleteFileMutation.mutate();
  };

  return (
    <FileTableRow file={props.file} onDelete={dialog.open}>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={dialog.isOpen}
        onClose={dialog.close}
      >
        <SupportSnackbar
          isOpen={deleteFileMutation.isError}
          onClose={deleteFileMutation.reset}
          message="An error occurred while deleting the file."
        />
        <DialogTitle>Delete file</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={1}>
            Are you sure you want to delete this file? The file will be
            permanently removed from the ticket.
          </Typography>
          <Typography>
            (Note: your audit log will continue to show a record of the deleted
            file.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={dialog.close}>
            No, cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={handleClickDelete}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
    </FileTableRow>
  );
}

interface Props {
  ticketKey: string;

  files: File[];
}

export function TicketFileTable(props: Props) {
  if (props.files.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          textAlign: 'center',
          minHeight: '200px',
        }}
      >
        <UploadFileOutlined
          sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No attachments yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: '400px', marginBottom: 3 }}
        >
          Attachments help you store files related to this ticket
        </Typography>
      </Card>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>File name</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.files.map((file) => (
            <TicketFileTableRow
              key={file.id}
              file={file}
              ticketKey={props.ticketKey}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
